import { useId } from "react";

import { ArrowUpRightIcon, BankIcon } from "@phosphor-icons/react";
import { Button, Card, Flex, Text } from "@radix-ui/themes";

import { formatCurrency } from "../overview.format";
import type { CashCardPresentationProps } from "../types";

const UNAVAILABLE = "—";

const PAPER_FUNDING_NOTE =
  "Deposits and withdrawals are available on a live account only.";

export const CashCardPresentation = ({
  cash,
  cashWithdrawable,
  onFund,
  onWithdraw,
  isPaperAccount,
  isLoading,
  hasError,
}: CashCardPresentationProps) => {
  const paperNoteId = useId();
  const describedBy = isPaperAccount ? paperNoteId : undefined;

  return (
    <Card size="2" className="flex-1">
      <Flex direction="column" gap="4" height="100%">
        <Flex direction="column" gap="1">
          <Text size="1" color="gray" weight="medium">
            Funds
          </Text>
          <Text size="4" weight="bold">
            Cash
          </Text>
        </Flex>

        <Flex direction="column" gap="3">
          <Balance
            label="Cash balance"
            value={cash}
            isLoading={isLoading}
            hasError={hasError}
            emphasize
          />
          <Balance
            label="Cash withdrawable"
            value={cashWithdrawable}
            isLoading={isLoading}
            hasError={hasError}
          />
        </Flex>

        <Flex direction="column" gap="2" className="mt-auto">
          {isPaperAccount ? (
            <Text as="p" id={paperNoteId} size="1" color="gray">
              {PAPER_FUNDING_NOTE}
            </Text>
          ) : null}
          <Flex gap="2" wrap="wrap">
            <Button
              size="2"
              className="flex-[1_1_120px]"
              disabled={isPaperAccount}
              aria-describedby={describedBy}
              onClick={onFund}
            >
              <BankIcon size={16} weight="bold" />
              Fund
            </Button>
            <Button
              size="2"
              variant="outline"
              color="gray"
              className="flex-[1_1_120px]"
              disabled={isPaperAccount}
              aria-describedby={describedBy}
              onClick={onWithdraw}
            >
              <ArrowUpRightIcon size={16} weight="bold" />
              Withdraw
            </Button>
          </Flex>
        </Flex>
      </Flex>
    </Card>
  );
};

const Balance = ({
  label,
  value,
  emphasize = false,
  isLoading,
  hasError,
}: {
  label: string;
  value?: number;
  emphasize?: boolean;
  isLoading: boolean;
  hasError: boolean;
}) => {
  const text =
    value !== undefined
      ? formatCurrency(value)
      : hasError
        ? UNAVAILABLE
        : isLoading
          ? "…"
          : UNAVAILABLE;

  return (
    <Flex align="end" justify="between" gap="3">
      <Text size="2" color="gray">
        {label}
      </Text>
      <Text
        size={emphasize ? "5" : "3"}
        weight="bold"
        className="atd-numeric whitespace-nowrap"
      >
        {text}
      </Text>
    </Flex>
  );
};
